import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usuarioService from '../services/UsuarioService';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import utnFrcLogo from '../assets/logos/utnFrcLogo.png';
import logoAve from '../assets/logos/logoave.png';
import RegionalService from '../services/RegionalService';
import regionalApiService from '../services/RegionalApiService';
import { isAdminUser, isGeneralAdmin } from '../utils/adminMenu';
import './Login.css';

const normalizeAccessContext = (context) => {
  if (!context) return null;

  const idRegional = context.idRegional || context.IdRegional || null;
  if (!idRegional) return null;

  return {
    idRegional,
    nombreRegional: context.nombreRegional || context.NombreRegional || context.nombre || context.Nombre || 'Regional',
    nombreRol: context.nombreRol || context.NombreRol || context.roleName || context.RoleName || '',
    activo: context.activo ?? context.Activo ?? true
  };
};

const normalizeAccessContexts = (contexts) =>
  Array.isArray(contexts)
    ? contexts
        .map(normalizeAccessContext)
        .filter(Boolean)
    : [];

const hasMultipleRoleContexts = (contexts) => {
  const distinctRoles = new Set(
    (Array.isArray(contexts) ? contexts : [])
      .map((context) => String(context?.nombreRol || '').trim().toLowerCase())
      .filter(Boolean)
  );

  return distinctRoles.size > 1;
};

const buildRegionalFromContext = (context) => ({
  id: context.idRegional,
  Id: context.idRegional,
  nombre: context.nombreRegional,
  Nombre: context.nombreRegional,
  activo: context.activo ?? true,
  Activo: context.activo ?? true
});

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    type: 'error',
    title: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos
    if (!formData.email || !formData.password) {
      setAlert({
        show: true,
        title: 'Campos incompletos',
        message: 'Por favor completa todos los campos antes de continuar.',
        type: 'warning'
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Llamar al servicio de usuario para hacer login
      const response = await usuarioService.login(formData.email, formData.password);

      const roleNameValue = response.nombreRol || response.roleName || '';
      const roleContext = {
        roleName: roleNameValue,
        roleId: response.idRol
      };
      const isAdmin = isAdminUser(roleContext);
      const isAdminGeneral = isGeneralAdmin(roleContext);

      // Normalizar datos adicionales del login
      const rawRegionales = response.regionales || response.Regionales || [];
      const rawAreas = response.areas || response.Areas || [];
      const rawMaterias = response.materias || response.Materias || [];
      const rawFeatureFlags = response.ffRegional || response.FFRegional || [];
      const rawAccessContexts = response.contextosAcceso || response.ContextosAcceso || [];
      const rawCurrentAccessContext = response.contextoAccesoActual || response.ContextoAccesoActual || null;
      const onlyActivos = (list) =>
        Array.isArray(list)
          ? list.filter((item) => {
              const activo = item?.activo ?? item?.Activo;
              return activo === undefined ? true : Boolean(activo);
            })
          : [];
      const regionales = onlyActivos(rawRegionales);
      const areas = onlyActivos(rawAreas);
      const materias = onlyActivos(rawMaterias);
      const featureFlags = Array.isArray(rawFeatureFlags) ? rawFeatureFlags : [];
      const accessContexts = normalizeAccessContexts(rawAccessContexts);
      const mixedRoleContexts = hasMultipleRoleContexts(accessContexts);
      const currentAccessContext = mixedRoleContexts
        ? null
        : normalizeAccessContext(rawCurrentAccessContext) || (accessContexts.length === 1 ? accessContexts[0] : null);
      const requiresAccessContextSelection = mixedRoleContexts || Boolean(
        response.requiereSeleccionContexto ??
        response.RequiereSeleccionContexto ??
        false
      );
      
      // Si llegamos aquí, el login fue exitoso (response.ok === true)
      const userData = {
        email: response.email,
        name: response.displayName,
        token: response.token,
        pictureUrl: response.pictureUrl || '',
        pertmiteCrearAsistente: response.pertmiteCrearAsistente || false,
        statusCode: response.statusCode,
        idRol: response.idRol,
        nombreRol: response.nombreRol,
        refreshToken: response.refreshToken || response.RefreshToken || '',
        requirePasswordChange: Boolean(response.requirePasswordChange ?? response.RequirePasswordChange ?? false),
        availableAccessContexts: accessContexts,
        currentAccessContext,
        requiresAccessContextSelection
      };

      if (requiresAccessContextSelection) {
        RegionalService.clearRegionalData();
        await login(userData);
        navigate('/select-access-context', { replace: true });
        return;
      }

      // Guardar regionales, áreas y materias en localStorage
      RegionalService.setRegionalData({ regionales, areas, materias });
      RegionalService.setFeatureFlags(featureFlags);

      // Actualizar el contexto de autenticación antes de cualquier llamado autenticado
      // posterior (por ejemplo el prefetch de regionales para admin general más abajo).
      await login(userData);

      // Determinar regional activo y ruta de redirección
      let redirectPath = '/dashboard';
      if (currentAccessContext) {
        const contextualRole = {
          roleName: currentAccessContext.nombreRol
        };
        const matchingRegional = regionales.find((regional) => {
          const regionalId = regional?.id || regional?.Id;
          return regionalId === currentAccessContext.idRegional;
        });
        RegionalService.setActiveRegional(matchingRegional || buildRegionalFromContext(currentAccessContext));
        redirectPath = isAdminUser(contextualRole) ? '/admin-dashboard' : '/dashboard';
      } else if (isAdmin) {
        if (isAdminGeneral) {
          RegionalService.setActiveRegional(null);
          redirectPath = '/admin/select-regional';
          try {
            // Prefetch de regionales para admin general (puede fallar sin bloquear flujo)
            const remote = await regionalApiService.getRegionales();
            const remoteList = remote.regionales || remote.Regionales || remote.data || remote || [];
            if (Array.isArray(remoteList) && remoteList.length > 0) {
              RegionalService.setRegionalData({ regionales: remoteList, areas, materias });
            }
          } catch (prefetchError) {
            console.warn('No se pudo prefetchear regionales admin:', prefetchError);
          }
        } else {
          RegionalService.setActiveRegional(regionales[0] || null);
          redirectPath = '/admin-dashboard';
        }
      } else if (regionales.length === 1) {
        RegionalService.setActiveRegional(regionales[0]);
      } else if (regionales.length > 1) {
        RegionalService.setActiveRegional(null);
        redirectPath = '/select-regional';
      } else {
        RegionalService.setActiveRegional(null);
      }

      // Redirigir al dashboard después del login exitoso
      navigate(redirectPath, { replace: true });
      
    } catch (error) {
      console.error('❌ Error en login:', error);
      
      // Mostrar mensaje priorizando el que devuelve el backend
      let errorMessage = error?.message || 'Error al iniciar sesión. Por favor, intenta nuevamente.';

      const lowerMsg = (errorMessage || '').toLowerCase();

      if (lowerMsg.includes('401') || lowerMsg.includes('unauthorized')) {
        errorMessage = 'Email o contraseña incorrectos. Verifica tus credenciales e intenta nuevamente.';
      } else if (lowerMsg.includes('404')) {
        errorMessage = 'Usuario no encontrado. Verifica tu email o solicita acceso al sistema.';
      } else if (lowerMsg.includes('500')) {
        errorMessage = 'Error del servidor. Por favor, intenta más tarde o contacta al administrador.';
      } else if (lowerMsg.includes('network')) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.';
      }
      
      setAlert({
        show: true,
        title: 'Error al iniciar sesión',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  return (
    <div className="login-container">
      {/* Background Pattern */}
      <div className="login-background">
        <div className="geometric-pattern"></div>
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="login-content">
        {/* Left Side - Branding */}
        <div className="login-branding">
          <div className="branding-content">
            <div className="branding-text">
              <h1 className="main-title">
                <span className="title-line-1">Departamento de</span>
                <span className="title-line-2">Ingeniería en Sistemas</span>
                <span className="title-line-3">de Información</span>
              </h1>
              <p className="subtitle">
                Universidad Tecnológica Nacional
              </p>
              <p className="subtitle-secondary">
                Regional Córdoba
              </p>
            </div>

            <div className="features-list">
              <div className="feature-item">
                <div className="feature-icon">
                  <i className="fas fa-robot"></i>
                </div>
                <span>Asistentes IA especializados</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <i className="fas fa-graduation-cap"></i>
                </div>
                <span>Contenido académico actualizado</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <i className="fas fa-shield-alt"></i>
                </div>
                <span>Plataforma segura y confiable</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-form-section">
          <div className="form-container">
            <div className="form-logos-container">
              <img 
                src={utnFrcLogo} 
                alt="UTN FRC Logo" 
                className="form-logo utn-logo-form"
              />
              <img 
                src={logoAve} 
                alt="Logo Ave" 
                className="form-logo ave-logo-form"
              />
            </div>
            
            <div className="form-header">
              <h2 className="form-title">Bienvenido</h2>
              <p className="form-subtitle">
                Accede a tu plataforma de asistencia académica
              </p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="input-group">
                <label htmlFor="email" className="input-label">
                  Correo Electrónico
                </label>
                <div className="input-wrapper">
                  <i className="fas fa-envelope input-icon"></i>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="tu.email@frc.utn.edu.ar"
                    required
                  />
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="password" className="input-label">
                  Contraseña
                </label>
                <div className="input-wrapper">
                  <i className="fas fa-lock input-icon"></i>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={togglePasswordVisibility}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              <div className="form-options">
                <label className="checkbox-wrapper">
                  <input type="checkbox" className="form-checkbox" />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">Recordar sesión</span>
                </label>
                <button
                  type="button"
                  className="forgot-link"
                  onClick={() => alert('Funcionalidad de recuperación de contraseña - Próximamente')}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <button 
                type="submit" 
                className={`login-button ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    <span>Verificando...</span>
                  </>
                ) : (
                  <>
                    <span>Ingresar</span>
                    <i className="fas fa-arrow-right"></i>
                  </>
                )}
              </button>
            </form>

            <div className="form-footer">
              <p className="footer-text">
                ¿No tienes cuenta? 
                <button 
                  type="button"
                  className="register-link"
                  onClick={() => alert('Solicitud de acceso - Próximamente')}
                > Solicitar acceso</button>
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Loading Spinner */}
      {isLoading && (
        <LoadingSpinner 
          message="Iniciando sesión..." 
          size="medium" 
          color="primary" 
        />
      )}
      
      {/* Alert Component */}
      <Alert
        title={alert.title}
        message={alert.message}
        type={alert.type}
        isVisible={alert.show}
        onClose={closeAlert}
      />
    </div>
  );
};

export default Login;
